import React, { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';

import { IParams } from 'presentation/entities/params.entity';
import { useCourse } from 'presentation/hooks/courses';
import { usePlaylist } from 'presentation/hooks/playlist';

import './styles.scss';
import { ToroLearnIcon } from '../icon';
import { trackingEvent } from 'data/usecases/tracking-event';

interface ILessonBadge {
    moduleIndex: number;
    lessonIndex: number;
    isCompleted: boolean;
}

interface IToroLearnPlayListProps {
    isSideBar?: boolean;
}
export const ToroLearnPlaylist = ({ isSideBar = false }: IToroLearnPlayListProps): JSX.Element => {
    const { course, isLocked, handleCompleteLesson } = useCourse();
    const { moduleId: moduleParam, lessonId: lessonParam } = useParams<IParams>();
    const { setParams, handleOpenCloseDropdown, dropdownActive, setSideBarActive } = usePlaylist();

    const moduleId = moduleParam && (parseInt(moduleParam, 10) - 1).toString();
    const lessonId = lessonParam && (parseInt(lessonParam, 10) - 1).toString();

    useEffect(() => {
        if (moduleId && lessonId) setParams({ moduleId, lessonId });
    }, [lessonId, moduleId, setParams]);

    const getLessonBadge = ({ moduleIndex, isCompleted, lessonIndex }: ILessonBadge): JSX.Element => {
        if (isLocked && moduleIndex > 1) {
            return <ToroLearnIcon icon="lock" size={15} fill="#7E868E" />;
        }

        return isCompleted ? (
            <button className={'btn btn-progress is-completed'}>
                <ToroLearnIcon icon="check" size={8} fill="white" />
            </button>
        ) : (
            <button className={'btn btn-progress'} onClick={() => handleCompleteLesson(moduleIndex, lessonIndex)}>
                <ToroLearnIcon icon="check" size={8} fill="white" />
            </button>
        );
    };

    if (!course) return <span></span>;

    const { modules } = course;

    return (
        <div className="playlist h-100 w-100 position-absolute">
            <div className="playlist-header d-flex align-items-center justify-content-between p-4">
                <h3 className="text-dark font-size-3">Aulas</h3>
                {!moduleId && (
                    <a type="button" onClick={() => setSideBarActive(false)}>
                        <ToroLearnIcon icon="cross" fill="dark" size={20} />
                    </a>
                )}
            </div>
            {modules.map(({ name: moduleName, lessons, progressPercent }, moduleIndex) => (
                <div key={`playlist-${moduleIndex}`} className="playlist-dropdown">
                    <button
                        className="btn btn-link text-left w-100"
                        onClick={() => handleOpenCloseDropdown(moduleIndex)}
                    >
                        <div className="d-flex align-items-center justify-content-between mb-3">
                            <div className="d-flex align-items-center">
                                {progressPercent === 100 ? (
                                    <div className="module-badge mr-2">
                                        {' '}
                                        <ToroLearnIcon icon="check" fill="white" size={8} />{' '}
                                    </div>
                                ) : (
                                    <span className="badge badge-dark mr-2">{progressPercent}%</span>
                                )}
                                <p
                                    className={`title mb-0 font-size-9 text-uppercase ${
                                        progressPercent === 100 ? 'text-success' : 'text-dark'
                                    } mr-2`}
                                >
                                    {`Módulo ${(moduleIndex + 1).toString().padStart(2, '0')}`}
                                </p>
                            </div>
                            {isLocked && moduleIndex < 2 && (
                                <span className="badge badge-pill bg-info text-dark text-uppercase mr-3">Grátis</span>
                            )}
                        </div>
                        <div className="d-flex align-items-center justify-content-between">
                            <p className="mb-0 title font-size-7 text-dark">{moduleName}</p>
                            <ToroLearnIcon
                                icon="keyboard-arrow-down"
                                size={7}
                                fill="gray-dark"
                                className={`${dropdownActive === moduleIndex ? 'icon-rotate' : ''}`}
                            />
                        </div>
                    </button>
                    <div className="playlist-item" aria-current={dropdownActive === moduleIndex}>
                        {lessons.map(({ name: lessonName, isCompleted }, lessonIndex) => (
                            <div key={`playlist-lesson-${moduleIndex}-${lessonIndex}`} className="position-relative">
                                <Link
                                    to={`/${course.slug}/${moduleIndex + 1}/${lessonIndex + 1}`}
                                    className={`d-block text-decoration-none ${
                                        moduleId === moduleIndex.toString() && lessonId === lessonIndex.toString()
                                            ? 'is-active'
                                            : 'is-not-active'
                                    } p-4`}
                                    onClick={() => {
                                        trackingEvent.elementClicked({
                                            productAreaOrigin: 'Aprenda',
                                            element: `Menu Aulas - ${isSideBar ? 'Módulo' : 'Curso'}`,
                                            group: course.modules[moduleIndex].name,
                                            label: course.modules[moduleIndex].lessons[lessonIndex].name,
                                            text: course?.name,
                                            timeResponsavel: 'Sky',
                                        });
                                    }}
                                >
                                    <small className="d-block text-gray-dark mb-1">
                                        Aula {(lessonIndex + 1).toString().padStart(2, '0')}
                                    </small>
                                    <p className="text-dark font-size-8 mb-0">{lessonName}</p>
                                </Link>
                                {
                                    <div className="lesson-badge">
                                        {getLessonBadge({ moduleIndex, lessonIndex, isCompleted })}
                                    </div>
                                }
                            </div>
                        ))}
                    </div>
                </div>
            ))}
        </div>
    );
};
